import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Modal, ModalBody, ModalFooter } from 'react-bootstrap'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { useSelector } from 'react-redux'
import { useGetCoursePrioritiesQuery, useSaveCoursePriorityMutation, useCreateCoursePriorityMutation } from '../api/courses'
import GlgInlineLoadingSpinner from '../../shared/components/glg_loading_spinner_inline'
import { showNotification, showErrorNotification } from '../../shared/helpers'

export const SetCoursePriorityModal = ({ show, setShow }) => {
  const playerPlural = useSelector((state) => state?.misc?.data?.playerPlural)
  const [orderedCourses, setOrderedCourses] = useState([])

  const { data, isLoading } = useGetCoursePrioritiesQuery(undefined, { skip: !show })
  const [saveCoursePriority, { isLoading: isSaving }] = useSaveCoursePriorityMutation()
  const [createCoursePriority] = useCreateCoursePriorityMutation()

  const displayCourses = orderedCourses.length > 0 ? orderedCourses : (data || [])

  const handleClose = () => {
    setOrderedCourses([])
    setShow(false)
  }

  const handleDragEnd = ({ source, destination }) => {
    if (!destination || destination.index === source.index) return
    const reordered = Array.from(displayCourses)
    const [moved] = reordered.splice(source.index, 1)
    reordered.splice(destination.index, 0, moved)
    setOrderedCourses(reordered)
  }

  const handleSave = async () => {
    const sortedCourses = displayCourses.reduce((acc, course, index) => {
      acc[course.id] = index + 1
      return acc
    }, {})
    try {
      await createCoursePriority(displayCourses.map((course) => course.id))
      await saveCoursePriority(sortedCourses).unwrap()
      showNotification(window.I18n.t('courses.set_course_priority.saved'))
      handleClose()
    } catch {
      showErrorNotification(window.I18n.t('courses.set_course_priority.save_error'))
    }
  }

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="text-center add-margin-top-20">
          <GlgInlineLoadingSpinner show={true} />
        </div>
      )
    }

    return (
      <div>
        <div className="btsp_notices add-margin-top-5 add-margin-bottom-15">
          {window.I18n.t('courses.set_course_priority.info', { player_plural: playerPlural })}
        </div>
        <div className="btsp_table_container padding-bottom-0">
          <table className="table table-drop table-striped index_table tables_v2 index_table courses_list no-margin-bottom">
            <thead>
              <tr className="header">
                <th className="width-10-percent">&nbsp;</th>
                <th className="width-10-percent text-align-center">
                  {window.I18n.t('courses.set_course_priority.priority')}
                </th>
                <th className="width-45-percent">
                  {window.I18n.t('courses.set_course_priority.name')}
                </th>
                <th className="width-35-percent hidden-sm hidden-xs">
                  {window.I18n.t('courses.set_course_priority.course_abbreviation')}
                </th>
              </tr>
            </thead>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="course-priority-list">
                {(provided) => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {displayCourses.map((course, index) => (
                      <Draggable key={course.id} draggableId={course.id} index={index}>
                        {(draggableProvided) => (
                          <tr
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                          >
                            <td className="width-10-percent add-padding-left-40" {...draggableProvided.dragHandleProps}>
                              <i className="fa fa-bars drag_and_drop_icon" />
                            </td>
                            <td className="width-10-percent text-align-center">{index + 1}</td>
                            <td className="width-45-percent">{course.name}</td>
                            <td className="width-35-percent hidden-sm hidden-xs">{course.name_abbreviation}</td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </DragDropContext>
          </table>
        </div>
      </div>
    )
  }

  return (
    <Modal show={show} onHide={handleClose} bsSize="large" backdrop="static">
      <div className="courses-modal-title-container">
        <div className="courses-modal-title">
          {window.I18n.t('courses.set_course_priority.title')}
        </div>
      </div>
      <ModalBody>
        <div className="btsp_square_solid">
          {renderBody()}
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="courses-modal-buttons-container">
          <button onClick={handleClose} className="cancel_action small grey button btsp_small_white_button">
            {window.I18n.t('buttons.cancel')}
          </button>
          <button onClick={handleSave} disabled={isSaving || isLoading} className="next_action small orange button btsp_small_orange_button">
            {window.I18n.t('buttons.save')}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  )
}

SetCoursePriorityModal.propTypes = {
  show: PropTypes.bool.isRequired,
  setShow: PropTypes.func.isRequired,
}
